import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, CircularProgress, Chip, IconButton, TextField, InputAdornment, Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { getCurrentMonthRange, isValidDateRange, getCurrentDate } from '../../../utils/dateUtils';
import Pagination from '../../shared/Pagination';
import './Index.css';

const PurchaseOrderIndex = () => {
    const { token, companyId, isAuthenticated, logout } = useAuth();
    const history = useHistory();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('All');
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    // Initialize with current month range
    const defaultRange = getCurrentMonthRange();
    const [fromDate, setFromDate] = useState(defaultRange.fromDate);
    const [toDate, setToDate] = useState(defaultRange.toDate);
    const [dateError, setDateError] = useState('');

    // Validate and correct dates on component mount
    useEffect(() => {
        const currentFromDate = fromDate;
        const currentToDate = toDate;
        
        // Check if current dates are invalid
        if (!isValidDateRange(currentFromDate, currentToDate)) {
            console.log('Invalid date range detected, correcting...', { currentFromDate, currentToDate });
            const correctedRange = getCurrentMonthRange();
            setFromDate(correctedRange.fromDate);
            setToDate(correctedRange.toDate);
            setDateError('');
        }
    }, []); // Run only on mount

    // Validate dates whenever they change
    useEffect(() => {
        if (fromDate && toDate && !isValidDateRange(fromDate, toDate)) {
            setDateError('Invalid date range: From date cannot be after To date');
        } else if (dateError && fromDate && toDate && isValidDateRange(fromDate, toDate)) {
            setDateError(''); // Clear error if dates become valid
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        // Only load orders if we have valid dates
        if (isValidDateRange(fromDate, toDate)) {
            loadOrders();
        }
    }, [isAuthenticated, token, fromDate, toDate]);

    // Date validation functions
    const handleFromDateChange = (newFromDate) => {
        setDateError('');
        
        if (!isValidDateRange(newFromDate, toDate)) {
            setDateError('From date cannot be after To date');
            return;
        }
        
        // Don't allow future dates
        if (new Date(newFromDate) > new Date(getCurrentDate())) {
            setDateError('From date cannot be in the future');
            return;
        }
        
        setFromDate(newFromDate);
    };

    const handleToDateChange = (newToDate) => {
        setDateError('');
        
        if (!isValidDateRange(fromDate, newToDate)) {
            setDateError('To date cannot be before From date');
            return;
        }
        
        // Don't allow future dates
        if (new Date(newToDate) > new Date(getCurrentDate())) {
            setDateError('To date cannot be in the future');
            return;
        }
        
        setToDate(newToDate);
    };

    // Function to fix invalid date ranges
    const fixDateRange = () => {
        const correctedRange = getCurrentMonthRange();
        setFromDate(correctedRange.fromDate);
        setToDate(correctedRange.toDate);
        setDateError('');
    };

    const loadOrders = async () => {
    setLoading(true);
    try {
        const url = `/${companyId}?fromDate=${fromDate}&toDate=${toDate}`;
        console.log('Fetching Purchase Orders with URL:', url);
        const response = await fetchData('PurchaseOrder', url, token);

        if (response.responseCode === '1111') {
            const converted = response.response_data.map(item => toCamelCase(item));
            setOrders(converted);  // <-- Correct camelCase binding
        } else {
            setOrders([]);
        }
    } catch (err) {
        console.error('Failed to fetch purchase orders', err);
        setOrders([]);
    } finally {
        setLoading(false);
    }
};
const toCamelCase = (obj) => {
    const newObj = {};
    for (let key in obj) {
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
        newObj[camelKey] = obj[key];
    }
    return newObj;
};


    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this purchase order?')) {
            try {
                await deleteData('PurchaseOrderAPI', `/${id}`, token);
                loadOrders();
            } catch (error) {
                console.error('Error deleting order:', error);
                alert('Failed to delete order');
            }
        }
    };



    const filteredOrders = orders.filter(order => {
        const matchesSearch =
            order.poNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customerName?.toLowerCase().includes(searchTerm.toLowerCase());

        let matchesFilter = true;
        if (filterType === 'Active') {
            matchesFilter = order.isActive === true;
        } else if (filterType === 'Inactive') {
            matchesFilter = order.isActive === false;
        }

        return matchesSearch && matchesFilter;
    });

    // Pagination logic
    const totalItems = filteredOrders.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterType]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    return (
        <div className="po-index-wrapper">
            {/* Header */}
            <div className="po-index-header">
                <div className="header-left">
                    <h1 className="index-title">Purchase Orders</h1>
                    
                </div>
                <Tooltip title="Create a new purchase order for stamping and AMC services" arrow>
                    <Button
                        variant="contained"
                        component={Link}
                        to="/PurchaseOrderCreate"
                        className="btn-create"
                        startIcon={<AddIcon />}
                    >
                        New Order
                    </Button>
                </Tooltip>
            </div>

            {/* Filters */}
            <div className="filters-section">
                {dateError && (
                    <div style={{ 
                        color: '#dc2626', 
                        fontSize: '14px', 
                        marginBottom: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span>{dateError}</span>
                        <Button
                            size="small"
                            onClick={fixDateRange}
                            style={{
                                marginLeft: '12px',
                                fontSize: '12px',
                                padding: '4px 8px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                textTransform: 'none'
                            }}
                        >
                            Fix Dates
                        </Button>
                    </div>
                )}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>From:</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => handleFromDateChange(e.target.value)}
                            max={getCurrentDate()}
                            style={{
                                padding: '8px 12px',
                                border: dateError ? '1px solid #dc2626' : '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>To:</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => handleToDateChange(e.target.value)}
                            min={fromDate}
                            max={getCurrentDate()}
                            style={{
                                padding: '8px 12px',
                                border: dateError ? '1px solid #dc2626' : '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        />
                    </div>
                </div>
                <TextField
                    placeholder="Search by PO Number or Customer..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }
                    }}
                />
                <div className="filter-chips">
                    {['All', 'Active', 'Inactive'].map((type) => (
                        <Chip
                            key={type}
                            label={type}
                            onClick={() => setFilterType(type)}
                            color={filterType === type ? 'primary' : 'default'}
                            className="filter-chip"
                        />
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-container">
                        <CircularProgress />
                        <p>Loading purchase orders...</p>
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📋</div>
                        <h3>No Purchase Orders Found</h3>
                        <p>Create your first purchase order to get started</p>
                        <Tooltip title="Create your first purchase order for stamping and AMC services" arrow>
                            <Button
                                variant="contained"
                                component={Link}
                                to="/PurchaseOrderCreate"
                                className="btn-create"
                                startIcon={<AddIcon />}
                            >
                                Create Order
                            </Button>
                        </Tooltip>
                    </div>
                ) : (
                    <TableContainer component={Paper} className="modern-table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className="table-header">PO Number</TableCell>
                                    <TableCell className="table-header">Customer</TableCell>
                                    <TableCell className="table-header">PO Date</TableCell>
                                    {/* <TableCell className="table-header">Stamp Type</TableCell>
                                    <TableCell className="table-header">Service Type</TableCell>
                                    <TableCell className="table-header">AMC Type</TableCell> */}
                                    <TableCell className="table-header">Status</TableCell>
                                    <TableCell className="table-header" align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedOrders.map((order) => (
                                    <TableRow key={order.id} className="table-row">
                                        <TableCell className="table-cell">
                                            <span className="po-number">{order.poNumber}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className="customer-name">{order.customerName || 'N/A'}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {order.poDate ? new Date(order.poDate).toLocaleDateString('en-GB') : '-'}
                                        </TableCell>
                                        {/* <TableCell className="table-cell">
                                            <span className="period-text">{order.stampTypeName || '-'}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className="period-text">{order.serviceTypeName || '-'}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className="period-text">{order.amcTypeName || '-'}</span>
                                        </TableCell> */}
                                        <TableCell className="table-cell">
                                            <Chip
                                                label={order.isActive ? 'Active' : 'Inactive'}
                                                color={order.isActive ? 'success' : 'default'}
                                                size="small"
                                                className="status-chip"
                                            />
                                        </TableCell>
                                        <TableCell className="table-cell" align="center">
                                            <div className="action-buttons">
                                                <Tooltip title="View order details" arrow>
                                                    <IconButton
                                                        size="small"
                                                        component={Link}
                                                        to={`/PurchaseOrderDetail/${order.id}`}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Edit this order" arrow>
                                                    <IconButton
                                                        size="small"
                                                        component={Link}
                                                        to={`/PurchaseOrderEdit/${order.id}`}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Delete this order" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(order.id)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>

                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>

            {/* Pagination */}
            {!loading && filteredOrders.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                />
            )}

            {/* Summary */}
            {!loading && filteredOrders.length > 0 && (
                <div className="summary-section">
                    <span className="summary-text">
                        Total: {orders.length} orders
                    </span>
                </div>
            )}
        </div>
    );
};

export default PurchaseOrderIndex;
